import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { expenseService, budgetService } from '../lib/api'
import { useAuth } from './AuthContext'
import { exportData } from '../utils/export'
import toast from 'react-hot-toast'

const ExpenseContext = createContext({})

const initialState = {
  expenses: [],
  budgets: [],
  budgetMeta: {
    monthlyBudget: 0,
    monthlyAllocated: 0,
    monthlyRemaining: 0,
    extraSources: []
  },
  loading: false,
  stats: {
    total: 0,
    count: 0,
    categoryBreakdown: {},
    recurringProjection: {
      monthlyEquivalent: 0,
      byFrequency: {
        daily: 0,
        weekly: 0,
        monthly: 0,
        yearly: 0
      }
    }
  },
  filters: {
    category: '',
    startDate: '',
    endDate: ''
  }
}

const expenseReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    
    case 'SET_EXPENSES':
      return { ...state, expenses: action.payload }
    
    case 'ADD_EXPENSE':
      return { ...state, expenses: [action.payload, ...state.expenses] }
    
    case 'UPDATE_EXPENSE':
      return {
        ...state,
        expenses: state.expenses.map(expense =>
          expense.id === action.payload.id ? action.payload : expense
        )
      }
    
    case 'DELETE_EXPENSE':
      return {
        ...state,
        expenses: state.expenses.filter(expense => expense.id !== action.payload)
      }
    
    case 'SET_BUDGETS':
      return { ...state, budgets: action.payload }

    case 'SET_BUDGET_META':
      return { ...state, budgetMeta: action.payload }
    
    case 'ADD_BUDGET':
      return { ...state, budgets: [action.payload, ...state.budgets] }
    
    case 'UPDATE_BUDGET':
      return {
        ...state,
        budgets: state.budgets.map(budget =>
          budget.id === action.payload.id ? action.payload : budget
        )
      }
    
    case 'DELETE_BUDGET':
      return {
        ...state,
        budgets: state.budgets.filter(budget => budget.id !== action.payload)
      }
    
    case 'SET_STATS':
      return { ...state, stats: action.payload }
    
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload } }
    
    default:
      return state
  }
}

export const useExpense = () => {
  const context = useContext(ExpenseContext)
  if (!context) {
    throw new Error('useExpense must be used within an ExpenseProvider')
  }
  return context
}

export const ExpenseProvider = ({ children }) => {
  const [state, dispatch] = useReducer(expenseReducer, initialState)
  const { user } = useAuth()

  // Load expenses when user changes
  useEffect(() => {
    if (user) {
      loadExpenses()
      loadBudgets()
    } else {
      dispatch({ type: 'SET_EXPENSES', payload: [] })
      dispatch({ type: 'SET_BUDGETS', payload: [] })
      dispatch({
        type: 'SET_BUDGET_META',
        payload: {
          monthlyBudget: 0,
          monthlyAllocated: 0,
          monthlyRemaining: 0,
          extraSources: []
        }
      })
    }
  }, [user])

  // Update stats when expenses change
  useEffect(() => {
    updateStats()
  }, [state.expenses])

  const loadExpenses = async (filters = {}) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      const expenses = await expenseService.getExpenses({ ...state.filters, ...filters })
      dispatch({ type: 'SET_EXPENSES', payload: expenses })
    } catch (error) {
      console.error('Error loading expenses:', error)
      toast.error('Failed to load expenses')
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  const addExpense = async (expenseData) => {
    try {
      // Validate required fields
      if (!expenseData.title || !expenseData.amount) {
        toast.error('Title and amount are required')
        throw new Error('Title and amount are required')
      }
      
      // Don't send user_id - backend gets it from JWT token
      const cleanedData = {
        title: expenseData.title,
        amount: parseFloat(expenseData.amount),
        category: expenseData.category || 'Other',
        description: expenseData.description || '',
        is_recurring: expenseData.is_recurring || false,
        recurring_frequency: expenseData.is_recurring ? (expenseData.recurring_frequency || null) : null
      }

      if (expenseData.budget_id) {
        cleanedData.budget_id = expenseData.budget_id
      }
      
      console.log('💰 Adding expense:', cleanedData)
      const expense = await expenseService.createExpense(cleanedData)
      dispatch({ type: 'ADD_EXPENSE', payload: expense })
      toast.success('Expense added successfully!')
      return expense
    } catch (error) {
      console.error('Error adding expense:', error)
      toast.error(error.message || 'Failed to add expense')
      throw error
    }
  }

  const updateExpense = async (id, updates) => {
    try {
      const expense = await expenseService.updateExpense(id, updates)
      dispatch({ type: 'UPDATE_EXPENSE', payload: expense })
      toast.success('Expense updated successfully!')
      return expense
    } catch (error) {
      console.error('Error updating expense:', error)
      toast.error('Failed to update expense')
      throw error
    }
  }

  const deleteExpense = async (id) => {
    try {
      await expenseService.deleteExpense(id)
      dispatch({ type: 'DELETE_EXPENSE', payload: id })
      toast.success('Expense deleted successfully!')
    } catch (error) {
      console.error('Error deleting expense:', error)
      toast.error('Failed to delete expense')
      throw error
    }
  }

  const loadBudgets = async () => {
    try {
      const budgetPayload = await budgetService.getBudgets()

      if (Array.isArray(budgetPayload)) {
        dispatch({ type: 'SET_BUDGETS', payload: budgetPayload })
        dispatch({
          type: 'SET_BUDGET_META',
          payload: {
            monthlyBudget: 0,
            monthlyAllocated: 0,
            monthlyRemaining: 0,
            extraSources: []
          }
        })
        return
      }

      dispatch({ type: 'SET_BUDGETS', payload: budgetPayload?.budgets || [] })
      dispatch({
        type: 'SET_BUDGET_META',
        payload: {
          monthlyBudget: budgetPayload?.monthly_budget || 0,
          monthlyAllocated: budgetPayload?.monthly_allocated || 0,
          monthlyRemaining: budgetPayload?.monthly_remaining || 0,
          extraSources: budgetPayload?.extra_sources || []
        }
      })
    } catch (error) {
      console.error('Error loading budgets:', error)
      toast.error('Failed to load budgets')
    }
  }

  const addBudget = async (budgetData) => {
    try {
      const budget = await budgetService.createBudget(budgetData)
      await loadBudgets()
      toast.success('Budget created successfully!')
      return budget
    } catch (error) {
      console.error('Error adding budget:', error)
      toast.error(error.message || 'Failed to create budget')
      throw error
    }
  }

  const updateBudget = async (id, updates) => {
    try {
      const budget = await budgetService.updateBudget(id, updates)
      await loadBudgets()
      toast.success('Budget updated successfully!')
      return budget
    } catch (error) {
      console.error('Error updating budget:', error)
      toast.error(error.message || 'Failed to update budget')
      throw error
    }
  }

  const deleteBudget = async (id) => {
    try {
      await budgetService.deleteBudget(id)
      await loadBudgets()
      toast.success('Budget deleted successfully!')
    } catch (error) {
      console.error('Error deleting budget:', error)
      toast.error(error.message || 'Failed to delete budget')
      throw error
    }
  }

  const updateStats = () => {
    const total = state.expenses.reduce((sum, expense) => sum + parseFloat(expense.amount), 0)
    const categoryBreakdown = state.expenses.reduce((acc, expense) => {
      const category = expense.category || 'Other'
      acc[category] = (acc[category] || 0) + parseFloat(expense.amount)
      return acc
    }, {})

    const recurringBaseByFrequency = {
      daily: 0,
      weekly: 0,
      monthly: 0,
      yearly: 0
    }

    state.expenses.forEach(expense => {
      if (!expense.is_recurring) return
      const frequency = (expense.recurring_frequency || 'monthly').toLowerCase()
      if (recurringBaseByFrequency[frequency] === undefined) return
      recurringBaseByFrequency[frequency] += parseFloat(expense.amount)
    })

    const recurringMonthlyEquivalent =
      recurringBaseByFrequency.daily * 30 +
      recurringBaseByFrequency.weekly * 4.33 +
      recurringBaseByFrequency.monthly +
      recurringBaseByFrequency.yearly / 12

    dispatch({
      type: 'SET_STATS',
      payload: {
        total,
        count: state.expenses.length,
        categoryBreakdown,
        recurringProjection: {
          monthlyEquivalent: recurringMonthlyEquivalent,
          byFrequency: recurringBaseByFrequency
        }
      }
    })
  }

  const setFilters = (filters) => {
    dispatch({ type: 'SET_FILTERS', payload: filters })
    loadExpenses(filters)
  }

  const exportExpenses = (format, filename = 'expenses') => {
    try {
      if (state.expenses.length === 0) {
        toast.error('No expenses to export')
        return
      }
      
      exportData(state.expenses, format, filename)
      toast.success(`Expenses exported to ${format.toUpperCase()}!`)
    } catch (error) {
      console.error('Error exporting expenses:', error)
      toast.error('Failed to export expenses')
    }
  }

  // Legacy CSV export method (keeping for backward compatibility)
  const exportToCSV = () => {
    exportExpenses('csv', 'expenses')
  }

  const value = {
    ...state,
    loadExpenses,
    addExpense,
    updateExpense,
    deleteExpense,
    loadBudgets,
    addBudget,
    updateBudget,
    deleteBudget,
    setFilters,
    exportToCSV,
    exportExpenses
  }

  return (
    <ExpenseContext.Provider value={value}>
      {children}
    </ExpenseContext.Provider>
  )
}
